import React, { ReactElement, useEffect, useRef, useState } from "react";

import axios from "axios";
import { v4 as uuid } from "uuid";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import UploadButton from "Components/UploadButton";

import { addMersDocument, getMersDocumentTypes } from "Adapters/Mers/mersDocumentAdapter";

import { getMersApiResponseError } from "Common/Utilities";

import { IMersDocumentApiResponse } from "Features/Vault/VaultInterfaces";

import {
  ExistingMersDocumentForAdd,
  isMersAddDocumentDuplicate,
} from "./isMersAddDocumentDuplicate";

export interface AddDocumentModalProps {
  existingMersDocuments: ExistingMersDocumentForAdd[];
  onClose: () => void;
  onSuccess: () => void;
  open: boolean;
  packageId: string;
}

async function computeSha256Base64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const base64 = btoa(String.fromCharCode(...hashArray));
  return base64;
}

function toSpacedLabel(type: string): string {
  return type.replace(/([A-Z])/g, " $1").trim();
}

const ALLOWED_EXTENSIONS = [".pdf", ".tif", ".tiff", ".jpg", ".jpeg", ".png"];

function fieldLabel(text: string): ReactElement {
  return (
    <Typography
      component="label"
      sx={{
        color: "text.secondary",
        display: "block",
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.02em",
        mb: 0.75,
        textTransform: "uppercase",
      }}
      variant="caption"
    >
      {text}
    </Typography>
  );
}

export function AddDocumentModal({
  existingMersDocuments,
  onClose,
  onSuccess,
  open,
  packageId,
}: AddDocumentModalProps): ReactElement {
  const [docTypes, setDocTypes] = useState<string[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [docTypesError, setDocTypesError] = useState<string>("");
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  // Kept only across a resubmit after a failure with no HTTP response; regenerated otherwise.
  const requestIdRef = useRef<string>("");
  if (!requestIdRef.current) {
    requestIdRef.current = uuid();
  }

  useEffect(() => {
    if (!open) {
      requestIdRef.current = uuid();
      setSelectedType(null);
      setFile(null);
      setFileError("");
      setSubmitError("");
    }
  }, [open]);

  useEffect(() => {
    if (!open || docTypes.length > 0) {
      return;
    }

    setLoadingTypes(true);
    setDocTypesError("");
    getMersDocumentTypes()
      .then(setDocTypes)
      .catch(() => {
        setDocTypesError("Unable to load document types. Please try again.");
      })
      .finally(() => {
        setLoadingTypes(false);
      });
  }, [open, docTypes.length]);

  function handleUploadError(error: string): void {
    requestIdRef.current = uuid();
    setFile(null);
    setFileError(error);
  }

  function handleTypeChange(value: string | null): void {
    if (value !== selectedType) {
      requestIdRef.current = uuid();
    }
    setSelectedType(value);
  }

  function handleFileUpload(selected: File): void {
    requestIdRef.current = uuid();
    setFileError("");
    const lowerCaseName = selected.name.toLowerCase();
    if (!ALLOWED_EXTENSIONS.some((extension) => lowerCaseName.endsWith(extension))) {
      setFileError("This file type isn't supported. Upload a PDF, TIFF, JPG, or PNG file.");
      setFile(null);
      return;
    }
    if (selected.size === 0) {
      setFileError("This file couldn't be read. Choose a different file.");
      setFile(null);
      return;
    }
    setFile(selected);
  }

  const submitDisabled =
    !selectedType || !file || !!fileError || submitting || loadingTypes || !!docTypesError;

  async function handleSubmit(): Promise<void> {
    if (!selectedType || !file || !!fileError || submitting) {
      return;
    }

    const computedHash = await computeSha256Base64(file);
    if (isMersAddDocumentDuplicate(existingMersDocuments, selectedType, computedHash, file.name)) {
      setSubmitError("A document of this type with identical content has already been added to this eNote.");
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    try {
      const data = await addMersDocument(packageId, selectedType, file, requestIdRef.current);
      const errorMessage = getMersApiResponseError(data);
      if (errorMessage) {
        requestIdRef.current = uuid();
        setSubmitError(errorMessage);
        return;
      }
      onSuccess();
    } catch (err: unknown) {
      // No response: the request may have committed, so keep the id.
      if (axios.isAxiosError(err) && err.response) {
        requestIdRef.current = uuid();
      }
      if (axios.isAxiosError(err)) {
        const errorMessage = getMersApiResponseError(err.response?.data as IMersDocumentApiResponse);
        setSubmitError(errorMessage ?? "An error occurred. Please try again.");
      } else {
        setSubmitError("An error occurred. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle>Add Document</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography sx={{ mb: 1 }} variant="body2">
              Add a supplemental document to this eNote and report it to MERS. This does not replace the eNote or
              affect its signature.
            </Typography>
            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
              <Typography component="li" variant="body2">
                Select the MERS document type that matches your file.
              </Typography>
              <Typography component="li" sx={{ mt: 0.5 }} variant="body2">
                Upload the PDF, TIFF, JPG, or PNG file.
              </Typography>
              <Typography component="li" sx={{ mt: 0.5 }} variant="body2">
                Click Submit to MERS.
              </Typography>
            </Box>
          </Box>

          <Box>
            {docTypesError && (
              <Typography color="error" sx={{ display: "block", mb: 1 }} variant="caption">
                {docTypesError}
              </Typography>
            )}
            <Autocomplete
              disabled={!!docTypesError}
              getOptionLabel={toSpacedLabel}
              loading={loadingTypes}
              onChange={(_, value) => handleTypeChange(value)}
              options={docTypes}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingTypes && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  label="Document Type (MERS)"
                  placeholder="Search document types..."
                />
              )}
              value={selectedType}
            />
          </Box>

          <Box>
            {fieldLabel("File")}
            <UploadButton
              fullWidth
              maxFileSizeBytes={26214400}
              maxFileSizeLabel="25 MB"
              onError={handleUploadError}
              onUpload={handleFileUpload}
            />
            {file && (
              <Typography sx={{ display: "block", mt: 1 }} variant="body2">
                Selected file: {file.name}
              </Typography>
            )}
            {fileError && (
              <Typography color="error" sx={{ display: "block", mt: 0.5 }} variant="caption">
                {fileError}
              </Typography>
            )}
          </Box>

          {submitting && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {submitError && (
            <Typography color="error" variant="caption">
              {submitError}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={submitDisabled} onClick={() => void handleSubmit()} variant="contained">
          {submitting ? "Submitting..." : "Submit to MERS"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
